// Screen-share state machine (preference 3, design/03 §3.2). Only one share is active at
// a time; whoever started it owns it until they stop. Pure + unit-tested; the CallScreen
// drives mediaClient.startScreenShare/stopScreenShare from these transitions.

export const SELF = "self";

export interface ScreenShareState {
  sharing: boolean;
  sharedBy: string | null; // SELF or a remote peerId
}

export const initialScreenShare: ScreenShareState = { sharing: false, sharedBy: null };

export type ScreenShareAction =
  | { type: "localStart" }
  | { type: "localStop" }
  | { type: "remoteStart"; peerId: string }
  | { type: "remoteStop"; peerId: string };

export function screenShareReducer(state: ScreenShareState, action: ScreenShareAction): ScreenShareState {
  switch (action.type) {
    case "localStart":
      return state.sharing ? state : { sharing: true, sharedBy: SELF };
    case "localStop":
      return state.sharedBy === SELF ? initialScreenShare : state;
    case "remoteStart":
      return state.sharing && state.sharedBy !== action.peerId ? state : { sharing: true, sharedBy: action.peerId };
    case "remoteStop":
      return state.sharedBy === action.peerId ? initialScreenShare : state;
    default:
      return state;
  }
}

export function canStartShare(state: ScreenShareState): boolean {
  return !state.sharing;
}

/** True if a producer's appData marks it as a screen share (mediaTag "screen" / share flag). */
export function isScreenShare(appData: Record<string, unknown> | undefined): boolean {
  return appData?.["mediaTag"] === "screen" || appData?.["share"] === true;
}

/** True when a remote peer is sharing — the screen pane should be rendered and bound. */
export function shouldShowScreenPane(state: ScreenShareState): boolean {
  return state.sharing && state.sharedBy !== SELF;
}

/** True when this client is the active screen sharer. */
export function isSelfSharing(state: ScreenShareState): boolean {
  return state.sharedBy === SELF;
}
